// Copyright (C) Unite Digital LLC - All Rights Reserved.
// Unauthorized copying of this file, via any medium is strictly prohibited.
// Proprietary and confidential.
// Developed by Unite Digital LLC.

import { Injectable } from '@angular/core';
import { environment } from '../../environments/environment';
import { BehaviorSubject, Observable, catchError, map, of } from 'rxjs';
import { ApiService } from './api.service';
import { Features } from '@interfaces/dealer.interfaces';

@Injectable({ providedIn: 'root' })
export class FeatureFlagService {
    private _features$: BehaviorSubject<any> = new BehaviorSubject(null);
    private _dealerId: number = null;

    get features() {
        return this._features$.value;
    }

    get dealerId() {
        return this._dealerId;
    }

    features$() {
        return this._features$.asObservable();
    }

    constructor(private readonly apiService: ApiService) {}

    setFeatures(features: any) {
        this._features$.next(features);
    }

    getFeatures(dealerId: number): Observable<any> {
        if (dealerId === this.dealerId && dealerId !== null) {
            return this.features$();
        } else {
            this._dealerId = dealerId;
            return this.apiService.getV2$(`features/${dealerId}`).pipe(map((response) => response));
        }
    }

    isDealerFeatureEnabled(dealerId: number, feature: Features): boolean {
        // 1. Verify the feature is turned on
        const featureFound = this.isFeatureEnabled(feature);

        //2. Verify the set dealer has the permission to use it
        let dealerHasSetting = false;
        if ((this.features?.dealerSettingFeatures || []).length > 0) {
            const setting = this.features.dealerSettingFeatures.find((s) => s.dealerId === dealerId && s.settingName === feature);

            if (setting && setting.settingValue === 'true') {
                dealerHasSetting = true;
            }
        }

        return featureFound && dealerHasSetting;
    }

    isFeatureEnabled(feature: Features): boolean {
        //Used for features that are not dealer specific.
        return this.features?.features[feature];
    }

    // Old way of managing feature flags...
    // TODO: Refactor these uses into the new approach
    // --------------------------------------------------------------------------------------------
    featureOff(featureName: string) {
        // Read the value from the config service
        if (this.getDefaultFeatureConfiguration(featureName)) {
            return !environment.features[featureName];
        }
        return true; // if feature not found, default to turned off
    }

    featureOn(featureName: string) {
        return !this.featureOff(featureName);
    }

    getDefaultFeatureConfiguration(featureName: string) {
        const foundFeature = Object.prototype.hasOwnProperty.call(environment.features, featureName);
        if (foundFeature) {
            return environment.features[featureName];
        }

        return false;
    }
    // --------------------------------------------------------------------------------------------
}
